import Enumerable from "linq";
import { useCallback } from "react";
import { useQuery } from "react-query";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import NavigationDataService from "../../services/navigation-data-service/NavigationDataService";
import { BaseUseQueryParameters } from "../BaseUseQueryParameters";
import { keys } from "./Keys";
import { RestError } from "../../services/rest-service/RestService";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";

export type ObjectDetailsTabsType = {
    hasValues: boolean;
    hasProperties: boolean;
    hasDefaults: boolean;
    hasAlarmSetpoints: boolean;
    hasAttachments: boolean;
    hasHistory: boolean;
    //hasMembership: boolean;
    hasRelationships: boolean;
    hasRunTickets: boolean;
};

export type UseAvailableObjectDetailsTabsParameters = BaseUseQueryParameters<ObjectDetailsTabsType | undefined>;

export function useAvailableObjectDetailsTabs({
    enabled,
    onError,
    onSuccess,
    refreshSeconds
}: UseAvailableObjectDetailsTabsParameters) {
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const routeContext = useRouteContext();

    const customerID = userState.customer?.id as number;
    const groupID = routeContext.groupID as number;
    const eLynxObject = routeContext.contextObject;
    const offline = offlineState.offline;

    return useQuery<Array<UIViewEnum>, RestError, ObjectDetailsTabsType>(
        keys.list(customerID, groupID, eLynxObject, offline),
        ({ signal }) => {
            return NavigationDataService.getAvailableViews(
                customerID,
                groupID,
                eLynxObject as IeLynxObject,
                offline ?? false,
                signal
            );
        },
        {
            select: useCallback(
                (data: Array<UIViewEnum>) => select(data, eLynxObject?.objectType),
                [eLynxObject?.objectType]
            ),
            enabled,
            onError: onError,
            onSuccess: onSuccess,
            refetchInterval: refreshSeconds ? refreshSeconds  * 1000 : false
        }
    );
}

function select(views: Array<UIViewEnum>, objectType?: eLynxObjectTypeEnum) {

    const viewMap = Enumerable.from(views).toDictionary(view => view);

    if (objectType === eLynxObjectTypeEnum.Device) {
        return {
            hasValues: viewMap.contains(UIViewEnum.Device_Details_Values),
            hasProperties: viewMap.contains(UIViewEnum.Device_Details_Properties),
            hasDefaults: viewMap.contains(UIViewEnum.Device_Details_Defaults),
            hasAlarmSetpoints: viewMap.contains(UIViewEnum.Device_Details_AlarmSetpoints),
            hasAttachments: viewMap.contains(UIViewEnum.Device_Details_Attachments),
            hasHistory: viewMap.contains(UIViewEnum.Device_Details_History),
            //hasMembership: viewMap.contains(UIViewEnum.Device_Details_Membership),
            hasRelationships: viewMap.contains(UIViewEnum.Device_Details_Relationships),
            hasRunTickets: viewMap.contains(UIViewEnum.Device_Details_RunTickets)
        } as ObjectDetailsTabsType;
    } else if (objectType === eLynxObjectTypeEnum.Well) {
        return {
            hasValues: viewMap.contains(UIViewEnum.Well_Details_Values),
            hasProperties: viewMap.contains(UIViewEnum.Well_Details_Properties),
            //hasDefaults: viewMap.contains(UIViewEnum.Well_Details_Details_Defaults),
            hasAlarmSetpoints: viewMap.contains(UIViewEnum.Well_Details_AlarmSetpoints),
            hasAttachments: viewMap.contains(UIViewEnum.Well_Details_Attachments),
            hasHistory: viewMap.contains(UIViewEnum.Well_Details_History),
            //hasMembership: viewMap.contains(UIViewEnum.Well_Details_Details_Membership),
            hasRelationships: viewMap.contains(UIViewEnum.Well_Details_Relationships),
            hasRunTickets: false
        } as ObjectDetailsTabsType;
    } else if (objectType === eLynxObjectTypeEnum.TankBattery) {
        return {
            hasValues: viewMap.contains(UIViewEnum.TankBattery_Details_Values),
            hasProperties: viewMap.contains(UIViewEnum.TankBattery_Details_Properties),
            //hasDefaults: viewMap.contains(UIViewEnum.TankBattery_Details_Details_Defaults),
            hasAlarmSetpoints: viewMap.contains(UIViewEnum.TankBattery_Details_AlarmSetpoints),
            hasAttachments: viewMap.contains(UIViewEnum.TankBattery_Details_Attachments),
            hasHistory: viewMap.contains(UIViewEnum.TankBattery_Details_History),
            //hasMembership: viewMap.contains(UIViewEnum.TankBattery_Details_Details_Membership),
            hasRelationships: viewMap.contains(UIViewEnum.TankBattery_Details_Relationships),
            hasRunTickets: viewMap.contains(UIViewEnum.TankBattery_Details_RunTickets)
        } as ObjectDetailsTabsType;
    } else if (objectType === eLynxObjectTypeEnum.Tank) {
        return {
            hasValues: viewMap.contains(UIViewEnum.Tank_Details_Values),
            hasProperties: viewMap.contains(UIViewEnum.Tank_Details_Properties),
            //hasDefaults: viewMap.contains(UIViewEnum.Tank_Details_Defaults),
            hasAlarmSetpoints: viewMap.contains(UIViewEnum.Tank_Details_AlarmSetpoints),
            hasAttachments: viewMap.contains(UIViewEnum.Tank_Details_Attachments),
            hasHistory: viewMap.contains(UIViewEnum.Tank_Details_History),
            //hasMembership: viewMap.contains(UIViewEnum.Tank_Details_Membership),
            hasRelationships: viewMap.contains(UIViewEnum.Tank_Details_Relationships),
            hasRunTickets: viewMap.contains(UIViewEnum.Tank_Details_RunTickets)
        } as ObjectDetailsTabsType;
    } else {
        return {
            hasValues: false,
            hasProperties: false,
            hasDefaults: false,
            hasAlarmSetpoints: false,
            hasAttachments: false,
            hasHistory: false,
            //hasMembership: false,
            hasRelationships: false,
            hasRunTickets: false,
        } as ObjectDetailsTabsType;
    }
}